'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { applicationsClient } from '@terra/shared-rest-clients-applications';
import { DIRECTORY_SCAN_STATUS_CHANGED_SSE_EVENT } from '@terra/shared-contracts-applications';
import { useEffect, useMemo } from 'react';
import { useSSE } from '../../app/providers/SseProvider';
import { getAuthHeaders } from '../../utils/authHeaders';
import { getCurrentGatewayUrl } from '../../utils/environmentDetection';
import { useProjectStore } from '../../store/projectStore';

const directoryScanQueryKey = (
  applicationId: string | undefined,
  scanId: string | undefined,
  tenantId: string | undefined,
) => ['directory-scan', applicationId, scanId, tenantId] as const;

const directoryScansListQueryKey = (
  applicationId: string | undefined,
  tenantId: string | undefined,
) => ['directory-scans', applicationId, tenantId] as const;

const orchestratorClientOptions = () => ({
  baseUrl: `${getCurrentGatewayUrl()}/orchestrator`,
});

export function useStartDirectoryScan({
  applicationId,
}: {
  applicationId?: string;
}) {
  const queryClient = useQueryClient();
  const selectedCompany = useProjectStore((state) => state.selectedCompany);
  const { tenantId: sseTenantId } = useSSE();
  const tenantId = sseTenantId ?? selectedCompany?.tenantId;

  return useMutation({
    mutationFn: async (payload: {
      wordlistSize: 'small' | 'medium' | 'large';
      httpMethod: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
      startPath?: string;
      credentialId?: string;
    }) => {
      if (!applicationId) {
        throw new Error('Application ID is required');
      }

      const authHeaders = await getAuthHeaders();
      const response = await applicationsClient.startDirectoryScan({
        params: { id: applicationId },
        body: payload,
        overrideClientOptions: orchestratorClientOptions(),
        extraHeaders: {
          ...authHeaders,
        },
      });

      if (response.status !== 200) {
        throw new Error('Failed to start directory scan');
      }

      return response.body;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: directoryScansListQueryKey(
          applicationId,
          tenantId ?? undefined,
        ),
      });
    },
  });
}

export function useDirectoryScans({
  applicationId,
  enabled = true,
}: {
  applicationId?: string;
  enabled?: boolean;
}) {
  const selectedCompany = useProjectStore((state) => state.selectedCompany);
  const { tenantId: sseTenantId } = useSSE();
  const tenantId = sseTenantId ?? selectedCompany?.tenantId;

  return useQuery({
    queryKey: directoryScansListQueryKey(applicationId, tenantId ?? undefined),
    queryFn: async () => {
      if (!applicationId || !tenantId) {
        return null;
      }

      const authHeaders = await getAuthHeaders();
      const response = await applicationsClient.listDirectoryScans({
        params: { id: applicationId },
        query: {},
        overrideClientOptions: orchestratorClientOptions(),
        extraHeaders: {
          ...authHeaders,
        },
      });

      if (response.status !== 200) {
        throw new Error('Failed to list directory scans');
      }

      return response.body;
    },
    enabled: !!applicationId && !!tenantId && enabled,
  });
}

export function useDirectoryScan({
  applicationId,
  scanId,
  enabled = true,
}: {
  applicationId?: string;
  scanId?: string;
  enabled?: boolean;
}) {
  const selectedCompany = useProjectStore((state) => state.selectedCompany);
  const { subscribe, tenantId: sseTenantId } = useSSE();
  const tenantId = sseTenantId ?? selectedCompany?.tenantId;
  const queryClient = useQueryClient();
  const queryKey = useMemo(
    () => directoryScanQueryKey(applicationId, scanId, tenantId ?? undefined),
    [applicationId, scanId, tenantId],
  );

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      if (!applicationId || !scanId || !tenantId) {
        return null;
      }

      const authHeaders = await getAuthHeaders();
      const response = await applicationsClient.getDirectoryScan({
        params: { id: applicationId, scanId },
        overrideClientOptions: orchestratorClientOptions(),
        extraHeaders: {
          ...authHeaders,
        },
      });

      if (response.status !== 200) {
        throw new Error('Failed to fetch directory scan');
      }

      return response.body;
    },
    enabled: !!applicationId && !!scanId && !!tenantId && enabled,
    refetchInterval: (current) => {
      const data = current.state.data;

      if (!data) {
        return false;
      }

      if (['RUNNING', 'ANALYZING', 'PENDING'].includes(data.status)) {
        return 5_000;
      }

      const hasPendingResults = data.results.some(
        (result) => result.verificationStatus === 'PENDING',
      );

      return hasPendingResults ? 5_000 : false;
    },
  });

  useEffect(() => {
    if (!applicationId || !scanId || !tenantId || !enabled) {
      return;
    }

    const unsubscribe = subscribe(
      DIRECTORY_SCAN_STATUS_CHANGED_SSE_EVENT,
      (event) => {
        if (event.scanId !== scanId || event.applicationId !== applicationId) {
          return;
        }

        void queryClient.invalidateQueries({ queryKey });
      },
      { filter: (event) => event.applicationId === applicationId },
    );

    return () => unsubscribe();
  }, [
    applicationId,
    enabled,
    queryClient,
    queryKey,
    scanId,
    subscribe,
    tenantId,
  ]);

  return query;
}
